import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ServicePackageService, {
  ICreateServicePackageParams,
} from '@clickersphere/core/services/ServicePackageService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  ServicePackageQueryData,
  SERVICE_PACKAGES_QUERY_KEY,
} from './useServicePackages';

export function useCreateServicePackage() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const {
    isPending: createServicePackageIsPending,
    mutateAsync: createServicePackageAsync,
  } = useMutation({
    mutationFn: async (data: ICreateServicePackageParams) => {
      const pkg = await ServicePackageService.create(data);
      return pkg as ServicePackageQueryData;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });

      const tempId = String(crypto.randomUUID());
      const now = new Date().toISOString();

      queryClient.setQueryData<ServicePackageQueryData[]>(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        (old) => [
          ...(old ?? []).concat({
            ...variables,
            id: tempId,
            tenantId: '',
            createdAt: now,
            updatedAt: now,
            queryStatus: 'pending',
          }),
        ],
      );

      return { tempId };
    },
    onSuccess: async (data, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });

      queryClient.setQueryData<ServicePackageQueryData[]>(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        (old) => old?.map((item) => (item.id === context.tempId ? data : item)),
      );

      toast(t('catalog.toasts.packageCreated'), 'success');
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });

      queryClient.setQueryData<ServicePackageQueryData[]>(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        (old) => old?.filter((item) => item.id !== context?.tempId),
      );

      toast(t('catalog.toasts.packageCreateError'), 'error');
    },
  });

  return {
    createServicePackageAsync,
    createServicePackageIsPending,
  };
}
